import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Box, Stack, Typography, IconButton, Skeleton, Collapse, Avatar } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { CV2_COLUMN_HEADER_HEIGHT } from "../../../constants/chatV2Constants";
import { IconDetailsSplit24, IconPageVisit16 } from "./ChatV2Icons";
import { IconInbox16 } from "../TicketV2/TicketV2Icons";
import { msgTimeWithSeconds } from "../../../utils/index";
import { colorCodesForAvatar } from "../../../constants/index";
import {
  getTrustchatGoogleMapsLoaderOptions,
  useGoogleMapsAuthFailed,
} from "../../../utils/googleMapsLoader";
import { getCustomerConversationHistory } from "../../../store/features/getCustomerConversationHistory";
import { mapConversationsToRows } from "../../../utils/conversationHistoryMappers";
import ConversationsHistoryModal from "../TicketV2/ConversationsHistoryModal";
import { HistoryPreviewSkeleton } from "../TicketV2/ticketDetails/TicketDetailsSelects";

const inter = '"Inter", sans-serif';

const emptyHistoryPlaceholderSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: 40,
  borderRadius: "8px",
  bgcolor: "#F9FAFA",
  color: "#405261",
  fontFamily: inter,
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "18px",
};

const mapPlaceholderSx = {
  height: 184,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px",
  textAlign: "center",
  color: "#405261",
  fontSize: 13,
  background: "#F4F6F8",
  borderRadius: "8px",
  boxSizing: "border-box",
};

function InfoField({ label, value, loading }) {
  return (
    <Box sx={{ width: "100%" }}>
      <Typography sx={{ fontSize: 12, color: "#405261", lineHeight: "16px", mb: 0.5 }}>
        {label}
      </Typography>
      {loading ? (
        <Skeleton width="80%" height={20} />
      ) : (
        <Typography
          sx={{
            fontSize: 14,
            color: "#061522",
            lineHeight: "24px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value || "( no answer )"}
        </Typography>
      )}
    </Box>
  );
}

function Section({ title, open, onToggle, children }) {
  return (
    <Box sx={{ borderBottom: "1px solid #E5E9EC" }}>
      <Box
        component="button"
        type="button"
        onClick={onToggle}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.5,
          px: 2,
          border: "none",
          bgcolor: "transparent",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#061522", lineHeight: "16px" }}>
          {title}
        </Typography>
        {open ? (
          <ExpandMoreIcon sx={{ fontSize: 16, width: 16, height: 16, color: "#405261" }} />
        ) : (
          <ChevronRightIcon sx={{ fontSize: 16, width: 16, height: 16, color: "#405261" }} />
        )}
      </Box>
      <Collapse in={open} unmountOnExit={false}>
        <Stack gap={2} sx={{ px: 2, pb: 2, display: open ? "flex" : "none" }}>
          {children}
        </Stack>
      </Collapse>
    </Box>
  );
}

function DetailsMap({ apiKey, center, loading }) {
  const loaderOpts = useMemo(
    () => getTrustchatGoogleMapsLoaderOptions(apiKey),
    [apiKey]
  );
  const { isLoaded, loadError } = useJsApiLoader(loaderOpts);
  const authFailed = useGoogleMapsAuthFailed();
  const [mapInstance, setMapInstance] = useState(null);

  const onLoad = useCallback((map) => {
    setMapInstance(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMapInstance(null);
  }, []);

  useEffect(() => {
    if (!mapInstance || !center?.lat || !center?.lng) return;
    try {
      mapInstance.panTo(center);
    } catch (_) {
      /* ignore pan errors while script reloads */
    }
  }, [mapInstance, center.lat, center.lng]);

  if (loadError || authFailed) {
    return (
      <Box sx={mapPlaceholderSx}>
        <Typography sx={{ fontWeight: 500, fontSize: 13 }}>Could not load map</Typography>
      </Box>
    );
  }

  // The map stays mounted while the next customer loads; a skeleton covers it
  // instead. Tearing it down would rebuild the map on every chat switch.
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: 184,
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      {isLoaded && (
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "184px" }}
          center={center}
          zoom={10}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{ fullscreenControl: false, mapTypeControl: false, streetViewControl: false }}
        >
          <Marker position={center} />
        </GoogleMap>
      )}
      {(loading || !isLoaded) && (
        <Skeleton
          variant="rectangular"
          height={184}
          width="100%"
          sx={{ position: "absolute", inset: 0, borderRadius: "8px" }}
        />
      )}
    </Box>
  );
}

function ChatDetails({
  customerData = [],
  getRoomId = [],
  customerLoading = false,
  customerId,
  sourceGroup = null,
  sourceLoading = false,
  onToggleDetails,
}) {
  const dispatch = useDispatch();
  const [openSections, setOpenSections] = useState({
    location: true,
    info: true,
    source: true,
    visited: true,
    history: true,
    apps: false,
  });
  const [showChatHistoryModal, setShowChatHistoryModal] = useState(false);
  const [chatHistoryPreview, setChatHistoryPreview] = useState([]);
  const [chatHistoryTotal, setChatHistoryTotal] = useState(0);
  const [chatHistoryLoading, setChatHistoryLoading] = useState(false);

  const customer = customerData?.[0] || {};
  const isCustomerLoading = !!customerLoading;
  const apiKey = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "").trim();
  const center = useMemo(
    () => ({
      lat: Number(customer.lat) || 31.5204,
      lng: Number(customer.lng) || 74.3587,
    }),
    [customer.lat, customer.lng]
  );

  const visitedPages = Array.isArray(customer.visited_pages) ? customer.visited_pages : [];

  const returningLabel =
    customer.visit_count || customer.chat_count || customer.ticket_count
      ? `${customer.visit_count || 0} visits, ${customer.chat_count || 0} chats, ${customer.ticket_count || 0} tickets.`
      : "";

  const toggle = (key) => setOpenSections((s) => ({ ...s, [key]: !s[key] }));

  const displayName = customer.name?.includes("Vistor")
    ? customer.ip_address
    : customer.name;

  const historyCustomerId =
    customerId || customer?.id || customer?._id || "";
  const historyCustomerName =
    (displayName && String(displayName)) || customer?.name || "Customer";

  useEffect(() => {
    if (!historyCustomerId) {
      setChatHistoryPreview([]);
      setChatHistoryTotal(0);
      setChatHistoryLoading(false);
      return undefined;
    }
    if (showChatHistoryModal) return undefined;

    let cancelled = false;
    setChatHistoryLoading(true);
    dispatch(
      getCustomerConversationHistory({
        customerId: historyCustomerId,
        page: 1,
        limit: 5,
      })
    )
      .then((res) => {
        if (cancelled) return;
        if (res.meta.requestStatus === "fulfilled") {
          const data = res.payload?.data || {};
          setChatHistoryPreview(mapConversationsToRows(data.conversations));
          setChatHistoryTotal(data.summary?.total || data.pagination?.total || 0);
        } else {
          setChatHistoryPreview([]);
          setChatHistoryTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) setChatHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, historyCustomerId, showChatHistoryModal]);

  // Clear stale preview immediately when switching customers.
  useEffect(() => {
    setChatHistoryPreview([]);
    setChatHistoryTotal(0);
  }, [historyCustomerId]);

  return (
    <>
      <ConversationsHistoryModal
        isOpen={showChatHistoryModal}
        onClose={() => setShowChatHistoryModal(false)}
        customerId={historyCustomerId}
        customerName={historyCustomerName}
      />

      <Box
        sx={{
          width: 280,
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          bgcolor: "#fff",
          overflow: "hidden",
        }}
      >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          height: CV2_COLUMN_HEADER_HEIGHT,
          minHeight: CV2_COLUMN_HEADER_HEIGHT,
          px: 2,
          borderBottom: "1px solid #E5E9EC",
          boxSizing: "border-box",
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 500, color: "#061522", lineHeight: "26px" }}>
          Details
        </Typography>
        <IconButton
          size="small"
          aria-label="Collapse details"
          onClick={onToggleDetails}
          sx={{ p: 0, width: 24, height: 24 }}
        >
          <IconDetailsSplit24 />
        </IconButton>
      </Stack>

      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", py: 1 }}>
        <Section title="Location" open={openSections.location} onToggle={() => toggle("location")}>
          {!apiKey ? (
            <Box sx={mapPlaceholderSx}>
              <Typography sx={{ fontWeight: 500, fontSize: 13 }}>Map unavailable</Typography>
            </Box>
          ) : (
            <DetailsMap apiKey={apiKey} center={center} loading={isCustomerLoading} />
          )}
        </Section>

        <Section title="Info" open={openSections.info} onToggle={() => toggle("info")}>
          <InfoField label="Name" value={displayName} loading={isCustomerLoading} />
          <InfoField label="Email" value={customer.email} loading={isCustomerLoading} />
          <InfoField
            label="Time"
            value={customer.time ? `${msgTimeWithSeconds(customer.time)} local time` : ""}
            loading={isCustomerLoading}
          />
          <InfoField label="Location" value={customer.location} loading={isCustomerLoading} />
          <InfoField label="Returning Customer" value={returningLabel} loading={isCustomerLoading} />
          <InfoField
            label="Last Seen"
            value={customer.last_seen ? msgTimeWithSeconds(customer.last_seen) : ""}
            loading={isCustomerLoading}
          />
          <InfoField label="IP Address" value={customer.ip_address} loading={isCustomerLoading} />
          <InfoField label="OS/Device" value={customer.os} loading={isCustomerLoading} />
          <InfoField label="Browser" value={customer.browser} loading={isCustomerLoading} />
        </Section>

        <Section title="Source" open={openSections.source} onToggle={() => toggle("source")}>
          {sourceLoading ? (
            <Stack direction="row" alignItems="center" gap={1}>
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton width="40%" height={20} />
            </Stack>
          ) : sourceGroup?.name ? (
            <Stack direction="row" alignItems="center" gap={1.25}>
              {sourceGroup.image ? (
                <Avatar
                  src={sourceGroup.image}
                  alt={sourceGroup.name}
                  sx={{ width: 32, height: 32, fontSize: 13, fontWeight: 500 }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: 13,
                    fontWeight: 500,
                    bgcolor:
                      colorCodesForAvatar[
                        Math.max(
                          0,
                          Math.min(
                            colorCodesForAvatar.length - 1,
                            (sourceGroup.name.charAt(0).toUpperCase().charCodeAt(0) || 65) - 65
                          )
                        )
                      ],
                    color: "#fff",
                  }}
                >
                  {sourceGroup.name.charAt(0).toUpperCase()}
                </Avatar>
              )}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "#405261",
                    lineHeight: "16px",
                    mb: 0.25,
                  }}
                >
                  Group
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "#061522",
                    lineHeight: "20px",
                    textTransform: "capitalize",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {sourceGroup.name}
                </Typography>
              </Box>
            </Stack>
          ) : (
            <Box sx={emptyHistoryPlaceholderSx}>No group</Box>
          )}
        </Section>

        <Section title="Visited Pages" open={openSections.visited} onToggle={() => toggle("visited")}>
          {isCustomerLoading ? (
            <Stack gap={1}>
              <Skeleton width="70%" height={20} />
              <Skeleton width="90%" height={16} />
            </Stack>
          ) : (
            <>
          <Typography sx={{ fontSize: 14, color: "#405261", lineHeight: "24px" }}>
            {visitedPages.length
              ? `Viewed ${visitedPages.length} page${visitedPages.length === 1 ? "" : "s"}`
              : "No pages viewed"}
          </Typography>
          {visitedPages.slice(0, 4).map((page, idx) => {
            const title = page?.title || page?.name || "Page";
            const url = page?.url || page?.path || "";
            return (
              <Stack key={idx} direction="row" gap={1} alignItems="flex-start">
                <Box sx={{ mt: "2px", flexShrink: 0 }}>
                  <IconPageVisit16 />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "#061522",
                      lineHeight: "16px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "#405261",
                      lineHeight: "24px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {url}
                  </Typography>
                </Box>
              </Stack>
            );
          })}
            </>
          )}
        </Section>

        <Section title="History" open={openSections.history} onToggle={() => toggle("history")}>
          {chatHistoryLoading && chatHistoryPreview.length === 0 ? (
            <HistoryPreviewSkeleton rows={5} />
          ) : chatHistoryPreview.length > 0 ? (
            <Stack gap={1}>
              {chatHistoryPreview.map((row) => (
                <Stack
                  key={row.id}
                  direction="row"
                  alignItems="center"
                  gap={1}
                  onClick={() => setShowChatHistoryModal(true)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setShowChatHistoryModal(true);
                    }
                  }}
                  sx={{
                    cursor: "pointer",
                    borderRadius: "8px",
                    "&:hover": { bgcolor: "#F9FAFA" },
                  }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <IconInbox16 />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "#405261",
                      fontFamily: inter,
                      fontWeight: 400,
                      lineHeight: "18px",
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.subject}
                  </Typography>
                </Stack>
              ))}
              <Typography
                component="button"
                type="button"
                onClick={() => setShowChatHistoryModal(true)}
                sx={{
                  mt: 0.5,
                  p: 0,
                  border: 0,
                  background: "none",
                  cursor: "pointer",
                  textAlign: "left",
                  fontFamily: inter,
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: "18px",
                  color: "#148DFD",
                  textDecoration: "underline",
                  textDecorationSkipInk: "none",
                  textUnderlineOffset: "4px",
                  "&:hover": { color: "#0B7AD9" },
                }}
              >
                View All {chatHistoryTotal}
              </Typography>
            </Stack>
          ) : (
            <Box sx={emptyHistoryPlaceholderSx}>No History</Box>
          )}
        </Section>

        <Section title="Apps" open={openSections.apps} onToggle={() => toggle("apps")}>
          <Typography sx={{ fontSize: 13, color: "#8B97A2" }}>No apps connected</Typography>
        </Section>
      </Box>
    </Box>
    </>
  );
}

export default ChatDetails;
